import { LinkRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router";
import { v4 as newGuid } from "uuid";
import { Autocomplete, SmallInputLabel, SnackbarContent, SurveyResponseAnswerView, TextEditor } from "..";
import { IssuePrivacyType, Links } from "../../constants";
import { useSession } from "../../hooks";
import { useCreateIssue, useGetTeams, useGetUsers } from "../../http";
import { ISurveyResponseAnswer, ITeamSummary, IUser } from "../../models";

interface UrlParams {
  slug: string;
}

interface ICreateIssueFormState {
  team: ITeamSummary | null;
  collaborators: IUser[];
  description: string;
  impact: string;
  privacy: IssuePrivacyType;
}

interface IProps {
  visible: boolean;
  context?: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  createdFromSurveyResponseAnswer?: ISurveyResponseAnswer;
  defaultSelectedTeamSlug?: string;
  onCreated: () => void;
  onCancelled: () => void;
}

const CreateIssueModal = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const location = useLocation();
  const { userId, displayName, profilePhotoSmallUrl } = useSession();
  const { mutate: createIssue, isLoading: isCreatingIssue } = useCreateIssue();
  const { isLoading: isLoadingTeams, data: teams } = useGetTeams(undefined, props.visible);
  const { isLoading: isLoadingUsers, data: users } = useGetUsers(props.visible);
  const { enqueueSnackbar } = useSnackbar();

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
    setValue,
  } = useForm<ICreateIssueFormState>({
    defaultValues: {
      team: null,
      collaborators: [],
      description: "",
      impact: "",
      privacy: IssuePrivacyType.Public,
    },
  });

  useEffect(() => {
    if (teams && props.visible) {
      setValue("team", teams.find((x) => x.slug === (props.defaultSelectedTeamSlug ?? slug)) ?? null);
    }
  }, [props.defaultSelectedTeamSlug, props.visible, setValue, slug, teams]);

  async function handleAddClicked() {
    const newIssueGuid = newGuid();

    handleSubmit((formState) => {
      createIssue(
        {
          newIssueGuid: newIssueGuid,
          description: formState.description,
          impact: formState.impact,
          teamSlug: formState.team?.slug,
          teamName: formState.team?.name,
          contextActionItemGuid: props.context?.actionItemGuid,
          createdFromSurveyResponseAnswerGuid: props.createdFromSurveyResponseAnswer?.answerGuid,
          createdByUser: { userId, displayName, profilePhotoSmallUrl, enabled: true },
          collaborators: formState.collaborators,
          privacy: formState.privacy,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });

            if (formState.team && Links.TeamImplementation(formState.team.slug) !== location.pathname) {
              enqueueSnackbar(
                <SnackbarContent
                  title="Loop Created"
                  content={
                    <>
                      Loop added to{" "}
                      <Link href={Links.TeamImplementation(formState.team.slug)}>{formState.team.name}</Link>
                    </>
                  }
                />,
                {
                  variant: "success",
                  action: <Button href={Links.IssueDetail(newIssueGuid)}>View Loop</Button>,
                },
              );
            }

            props.onCreated();
          },
        },
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Add Loop</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {props.context && (
            <Stack>
              <SmallInputLabel>Context</SmallInputLabel>
              <Stack direction="row" spacing={0.25} sx={{ alignItems: "start" }}>
                <LinkRounded sx={{ position: "relative", top: 4 }} />
                <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <Box component="span" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    {props.context?.teamName}
                  </Box>
                  {` : ${props.context?.actionItemDescription}`}
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack spacing={0.25}>
            <SmallInputLabel>Description *</SmallInputLabel>
            <Controller
              name="description"
              control={control}
              rules={{ required: { message: "Loop description is required", value: true } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors["description"] !== undefined}
                  helperText={errors["description"]?.message}
                  placeholder="Enter what you are noticing..."
                  autoFocus
                  multiline
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25}>
            <SmallInputLabel>Impact</SmallInputLabel>
            <Controller
              name="impact"
              control={control}
              render={({ field }) => (
                <TextEditor
                  initialValue={field.value}
                  placeholder="Explain how it impacts the team or organization..."
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
          <Divider variant="dashed" />

          <Stack spacing={0.25}>
            <SmallInputLabel>Privacy *</SmallInputLabel>
            <Controller
              name="privacy"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  renderValue={(selected: IssuePrivacyType) => {
                    if (selected === IssuePrivacyType.Public) {
                      return "Public to Organization";
                    } else {
                      return "Limited";
                    }
                  }}
                >
                  <MenuItem value={IssuePrivacyType.Public}>Public to Organization</MenuItem>
                  <MenuItem value={IssuePrivacyType.Limited}>
                    <Stack>
                      <Typography>Limited</Typography>
                      <Typography variant="caption" sx={{ lineHeight: "1rem" }}>
                        Visible only to creator, decision maker, collaborators, and assigned team members.
                        <br />
                        This setting cannot be changed later.
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              )}
            />
          </Stack>

          <Stack spacing={0.25}>
            <SmallInputLabel>Team To Process</SmallInputLabel>
            <Controller
              name="team"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingTeams}
                  disabled={isLoadingTeams}
                  options={teams ?? []}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <MenuItem component="li" {...props} key={option.slug}>
                      <Typography>{option.name}</Typography>
                    </MenuItem>
                  )}
                  onChange={(_, option) => field.onChange(option)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add a team to process..."
                      inputProps={{ ...params.inputProps }}
                      error={errors["team"] !== undefined}
                      helperText={errors["team"]?.message}
                    />
                  )}
                  value={field.value}
                  isOptionEqualToValue={(option, value) => option.slug === value.slug}
                />
              )}
            />
          </Stack>

          <Stack spacing={0.25}>
            <SmallInputLabel>Collaborators</SmallInputLabel>
            <Controller
              name="collaborators"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isLoadingUsers}
                  disabled={isLoadingUsers || !users}
                  options={users ?? []}
                  getOptionLabel={(option) => option.displayName}
                  onChange={(_, selectedUsers) => field.onChange(selectedUsers)}
                  renderOption={(props, option) => (
                    <MenuItem component="li" {...props} key={option.userId}>
                      <Stack>
                        <Typography>{option.displayName}</Typography>
                        <Typography variant="caption">{option.email}</Typography>
                      </Stack>
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add additional collaborators..."
                      inputProps={{ ...params.inputProps }}
                    />
                  )}
                  value={field.value}
                  isOptionEqualToValue={(option, value) => option.userId === value.userId}
                  multiple
                />
              )}
            />
          </Stack>

          {props.createdFromSurveyResponseAnswer && (
            <Stack spacing={1}>
              <Typography>Created loop will reference the survey response: </Typography>
              <SurveyResponseAnswerView surveyResponseAnswer={props.createdFromSurveyResponseAnswer} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isCreatingIssue} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isCreatingIssue}
          endIcon={isCreatingIssue ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleAddClicked}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateIssueModal };
